import type { CSSProperties } from 'react'
import { readTierConfig, resolveCardTier, tierDisplay, tierPlacement, tierPopover, tierTintStyle, type CardTier, type TierPopoverConfig } from './tier-rating'
import { TierTrigger } from './TierPopover'
import type { ExploreProperty } from '../../widgets/explorer/data'
import type { TierDisplay, TierPlacement, TierRatingConfig } from '../../widgets/explorer/tier-rating/schema'
import './tier-rating.css'

/**
 * Tier badge (#1813). One markup contract mirroring the toolkit's
 * `Renderer::badge()` / `tier-rating-frontend.js badge()`: the same
 * `hrtk-tier*` classes and data attributes, so a site's `--hrtk-tier-*`
 * theming and per-tier CSS carry over. With the popover on (toolkit default,
 * #1821) the badge is the disclosure `<button>` with the info glyph
 * (TierPopover.tsx); switched off it is the toolkit's static variant
 * (`hrtk-tier__badge--static`) with the description as the tooltip.
 */
export function TierBadge({ tier, display, placement, popover = null }: {
  tier: CardTier
  display: TierDisplay
  placement: TierPlacement
  popover?: TierPopoverConfig | null
}) {
  const label = tier.label.trim()
  if (!label) return null
  let show: TierDisplay = display
  const image = tier.image.trim()
  const stars = Math.max(0, Math.min(5, tier.stars))
  // "image" is for art that carries the tier name: the image alone is the
  // badge, no pill. A tier without an image gets the full badge instead.
  if (show === 'image' && image === '') show = 'both'
  const imageOnly = show === 'image'
  const hasVisual = stars > 0 || image !== ''
  // "stars" with nothing visual to show degrades to the label: a badge is never empty.
  const labelVisible = (show !== 'stars' && !imageOnly) || !hasVisual
  const showVisual = show !== 'badge' && hasVisual
  const description = tier.description.trim()

  const classes = `hrtk-tier hrtk-tier--${tier.slug} hrtk-tier--card hrtk-tier--show-${show}${placement === 'media' ? ' hrtk-tier--media' : ''}`
  const inner = (
    <>
      {showVisual && image !== '' ? (
        <img className="hrtk-tier__image" src={image} alt="" loading="lazy" decoding="async" />
      ) : showVisual && stars > 0 ? (
        <span className="hrtk-tier__stars" aria-hidden="true">
          {Array.from({ length: stars }, (_, i) => <span key={i} className="hrtk-tier__star">&#9733;</span>)}
        </span>
      ) : null}
      <span className={labelVisible ? 'hrtk-tier__label' : 'hrtk-tier__label hrtk-tier__label--sr'}>{label}</span>
    </>
  )
  return (
    <span
      className={classes}
      style={tierTintStyle(tier.color) as CSSProperties | undefined}
      data-hrtk-tier={tier.slug}
      data-hrtk-tier-label={label}
      data-hrtk-tier-description={description}
      data-hrtk-tier-rank={tier.rank}
    >
      {popover ? (
        <TierTrigger tier={tier} popover={popover} ariaLabel={`${label} tier. About our property ratings`}>
          {inner}
          {/* The art-only badge carries no glyph: the image alone is the badge. */}
          {!imageOnly && <span className="hrtk-tier__info" aria-hidden="true">i</span>}
        </TierTrigger>
      ) : (
        <span className="hrtk-tier__badge hrtk-tier__badge--static" title={description || undefined}>
          {inner}
        </span>
      )}
    </span>
  )
}

/**
 * The badge for one card at one placement, or null: null config, no matching
 * term, or the other placement. Templates call it twice (media slot + title
 * slot); `fallbackToTitle` lets a card without a photo keep the badge under
 * the title the way the toolkit's media placement degrades.
 */
export function CardTierBadge({ property, config, at, hasMedia = true }: {
  property: ExploreProperty
  config: TierRatingConfig | null | undefined
  at: TierPlacement
  hasMedia?: boolean
}) {
  const cfg = readTierConfig({ tierRating: config })
  if (!cfg) return null
  const want = tierPlacement(cfg)
  const effective: TierPlacement = want === 'media' && !hasMedia ? 'title' : want
  if (effective !== at) return null
  const tier = resolveCardTier(property, cfg)
  if (!tier) return null
  return <TierBadge tier={tier} display={tierDisplay(cfg)} placement={effective} popover={tierPopover(cfg)} />
}
